import { deepCopy } from '@utils/common/index'
import Template from './templates/default-template.vue'
import items from '@/lib/form/index'
import Options from '@utils/common/options/index'
import Select from '@utils/common/select'
import TableSelect from '@utils/table-select'
import DropdownOptions from '@/components/select/dropdown-options/dropdown-options.vue'
import Tinymce from '@/components/tinymce/index.js'
import http from '@plugin/axios'

import OptionsSelect from '@/components/select/options-select/options-select.vue'
import SearchSelectForm from '@/components/select/search-select-form/search-select-form.vue'

type FormType = 'default' | 'add' | 'modify'

interface FormItem {
  component: string | object;
  [prop: string]: any;
}

interface Col {
  span?: number;
  offset?: number;
  push?: number;
  pull?: number;
  xs?: Col;
  sm?: Col;
  md?: Col;
  lg?: Col;
  xl?: Col;
  tag?: string;
}

interface GroupItem {
  prop: string | string[];
  mapKey?: string;
  label?: string;
  formItem?: FormItem;
  rules?: object[];
  col?: Col;
  default?: any;
  on?: { [event: string]: any };
  show?: boolean | ((vm: any) => boolean);
  [prop: string]: any;
}

interface Group {
  title: string | object;
  subTitle?: any;
  group: Array<GroupItem[]>;
}

function getOptions(options: Group[]): Group[] {
  if (!options.length) return [{ title: '', group: [[]] }]
  return options
}

function getRules(rule: any | any[], prefix: string): object[] {
  if (Array.isArray(rule)) {
    return rule.map(item => {
      let message
      if (item.message) {
        message = prefix && item.message.indexOf('.') === -1 ? `${prefix}.${item.message}` : item.message
      }
      return { ...item, message }
    })
  } else {
    let message
    if (rule.message) {
      message = prefix && rule.message.indexOf('.') === -1 ? `${prefix}.${rule.message}` : rule.message
    }
    return [{ ...rule, message }]
  }
}

function groupForEach(group: Array<GroupItem | GroupItem[]>, cb: any) {
  group.forEach(items => {
    if (Array.isArray(items)) {
      groupForEach(items, cb)
    } else {
      cb(items)
    }
  })
}

function getCopyLibItem(item: GroupItem): GroupItem {
  const copy = { ...item }
  if (copy.formItem) {
    if (typeof copy.formItem === 'string') {
      copy.formItem = { component: copy.formItem }
    } else {
      const obj = deepCopy({ ...copy.formItem, component: '' })
      obj.component = copy.formItem.component
      copy.formItem = obj
    }
  }
  if (copy.col) {
    copy.col = deepCopy(copy.col)
  }
  if (copy.on) {
    copy.on = deepCopy(copy.on)
  }
  if (copy.rules) {
    copy.rules = getRules(copy.rules, '')
  }
  return copy
}

function getDataFromVm(vm: any, injectData?: object) {
  const data = vm.data || {}
  const params = vm.$route && vm.$route.params
  return Object.assign(data, params || {}, injectData)
}

function injectFormLib(items: GroupItem[], map: Map<string | string[], GroupItem>) {
  items.forEach(item => {
    const key = item.mapKey || item.prop
    map.set(key, item)
  })
}

class Form {
  static Select = Select

  static template: any = Template
  static lib = new Map()
  static injectFormLib = injectFormLib

  public vm: any = null
  public _name?: string
  public _autoCreateForm = true
  public prefix = ''
  public mixins: object = {}
  public form: Group[] = []
  public submitFn?: (data: any) => Promise<any>
  public getFn?: (data: any) => Promise<any>
  public _btns?: any[]

  public _type: FormType = 'add'
  public _span = 8
  private _group: Group = { title: '', group: [[]] }
  private _groupItem: GroupItem[] = []
  private _formItem: GroupItem = { prop: '' }
  private _formItemMap: Map<string | string[], { group: Group; item: GroupItem }> = new Map()

  constructor(options: Group[] | string = '') {
    if (typeof options === 'string') {
      this.prefix = options
      options = []
    }
    this.form = getOptions(options)
    if (this.form[this.form.length - 1]) {
      this._group = this.form[this.form.length - 1]
    }
    const group = this._group.group
    this._groupItem = group[group.length - 1]
    if (!this._groupItem || !Array.isArray(this._groupItem)) {
      // group不存在groupitem时自动添加一个
      // 如果options最后一个formitem不是数组，则另启一行
      this._groupItem = []
      group.push(this._groupItem)
    }
    this._formItem = this._groupItem[this._groupItem.length - 1]
    this.getFormItemMap()
  }

  private addFormItemMap() {
    if (!this._formItem) return
    const key = this._formItem.mapKey || this._formItem.prop
    this._formItemMap.set(key, { item: this._formItem, group: this._group })
  }

  private getFormItemMap() {
    const itemMap = new Map()
    this.form.forEach(group => {
      groupForEach(group.group, (item: any) => {
        if (item.prop) {
          const key = item.mapKey || item.prop
          itemMap.set(key, { item, group })
        }
      })
    })
    this._formItemMap = itemMap
  }

  btns(btns: any[]) {
    this._btns = btns
    return this
  }
  // 全局配置方法
  // 获取数据url
  dataUrl(url: string | ((vm: any) => any), baseURL?: string, prop = 'id', filter = (i: any) => i) {
    this._autoCreateForm = false
    this._type = 'modify'
    if (typeof url === 'function') {
      this.getFn = async() => {
        await url(this.vm)
      }
      return this
    }
    this.getFn = async() => {
      const vm = this.vm
      if (!vm) return Promise.resolve()
      let data = await http({ url, baseURL, data: { [prop]: vm.$route.params[prop] }})
      data = await filter(data)
      vm.setData(data)
      vm.createForm()
    }
    return this
  }
  //  保存请求url
  url(url: string | ((data: any) => Promise<any>), baseURL?: string) {
    if (typeof url !== 'string') {
      this.submitFn = url
      return this
    }
    this.submitFn = (data) => {
      const vm = this.vm
      if (!vm) return Promise.resolve()
      return http({
        url,
        baseURL,
        data: getDataFromVm(vm, data)
      })
    }
    return this
  }

  setSpan(span = 8) {
    this._span = span
    return this
  }

  // 后面是添加form配置项的方法
  addGroup(title: string | Group = '') {
    if (typeof title === 'string') {
      title = title && this.prefix && title.indexOf('.') === -1 ? `${this.prefix}.${title}` : title
      const newGroup = { title, group: [] }
      this.form.push(newGroup)
      this._group = newGroup
    } else {
      this.form.push(title)
      this._group = title
    }
    this._groupItem = []
    this._group.group.push(this._groupItem)
    return this
  }

  title(title: string | object) {
    if (typeof title === 'object') {
      this._group.title = title
      return this
    }
    this._group.title = this.prefix && title.indexOf('.') === -1 ? `${this.prefix}.${title}` : title
    return this
  }

  subTitle(subTitle: any) {
    this._group.subTitle = subTitle
    return this
  }

  get(prop: string) {
    const map = this._formItemMap.get(prop)
    if (!map) return this
    this._formItem = map.item
    return this
  }

  switch(prop: string) {
    const map = this._formItemMap.get(prop)
    if (!map) return this
    const { item, group } = map
    const groupItem = group.group.filter(gItem => {
      return gItem.includes(item)
    })
    if (!groupItem.length) return this
    this._group = group
    this._groupItem = groupItem[0]
    this._formItem = item
    return this
  }

  lib(prop: string) {
    const index = this._groupItem.indexOf(this._formItem)
    const item = Form.lib.get(prop)
    if (!item) return this
    this._formItem = getCopyLibItem(item)
    if (index === -1) {
      this._groupItem.push(this._formItem)
    } else {
      this._groupItem.splice(index + 1, 0, this._formItem)
    }
    this.addFormItemMap()
    return this
  }

  merge(obj: string | object) {
    const merge = typeof obj === 'string'
      ? Form.lib.get(obj) || {}
      : obj
    const oldProp = this._formItem.prop
    Object.assign(this._formItem, getCopyLibItem(merge), { mapKey: this._formItem.mapKey })
    if (oldProp === this._formItem.prop) return this
    this._formItemMap.delete(oldProp)
    this.addFormItemMap()
    return this
  }

  add(prop: string | string[] | GroupItem = '') {
    const index = this._groupItem.indexOf(this._formItem)
    this._formItem = typeof prop === 'string' || Array.isArray(prop)
      ? { prop, formItem: { component: 'el-input' }, col: { span: this._span }}
      : prop
    if (index === -1) {
      this._groupItem.push(this._formItem)
    } else {
      this._groupItem.splice(index + 1, 0, this._formItem)
    }
    this.addFormItemMap()
    let label
    if (typeof prop === 'string') {
      label = prop
    } else if (!Array.isArray(prop)) {
      label = prop.label
    }
    return this.label(label || '')
  }

  formItemComponent(prop: string | string[], formItemComponent: any, def: any = []) {
    this.add(prop)
    this.label('')
    this.col(24)
    this.default(def)
    this._formItem.formItemComponent = formItemComponent
    this._formItem.formItem = { component: '' }
    return this
  }

  table(prop: string, select: TableSelect) {
    this.addGroup()
    this.add(prop)
    this.label('')
    this.col(24)
    this.default([])
    this._formItem.formItemComponent = select.tpl()
    this._formItem.formItem = { component: '', $tableSelect: select }
    select.injectForm(this)
    return this
  }

  tinymce() {
    this.component(Tinymce)
    this.col(24)
    return this
  }

  br() {
    this._groupItem = []
    this._group.group.push(this._groupItem)
    return this
  }

  prop(prop: string | string[]) {
    if (!prop) return this
    const oldProp = this._formItem.prop
    if (oldProp) this._formItemMap.delete(oldProp)
    this._formItem.prop = prop
    this.addFormItemMap()
    return this
  }

  mapKey(mapKey: string) {
    const oldKey = this._formItem.prop || this._formItem.mapKey
    if (oldKey) this._formItemMap.delete(oldKey)
    this._formItem.mapKey = mapKey
    this.addFormItemMap()
    return this
  }

  label(label?: string) {
    if (!label) {
      this._formItem.label = ''
    } else {
      this._formItem.label = this.prefix && label.indexOf('.') === -1 ? `${this.prefix}.${label}` : label
    }
    return this
  }

  col(col: number | Col) {
    if (typeof col === 'number') {
      if (this._formItem.col) {
        this._formItem.col.span = col
      } else {
        this._formItem.col = { span: col }
      }
    } else {
      this._formItem.col = col
    }
    return this
  }

  default(value: any) {
    this._formItem.default = value
    return this
  }

  formItem(formItem: FormItem | string) {
    this._formItem.formItem = typeof formItem === 'string'
      ? { component: formItem }
      : formItem
    return this
  }

  show(show: boolean | ((vm: any) => boolean) = true) {
    this._formItem.show = show
    return this
  }

  component(component: string | object) {
    if (this._formItem.formItem) {
      this._formItem.formItem.component = component
    } else {
      this._formItem.formItem = { component }
    }
    return this
  }

  op(options: string | Options) {
    return this.formItem({
      component: OptionsSelect,
      _options: typeof options === 'string' ? Options.use(options) : options
    })
  }

  dropOp(options: string | Options) {
    const title = this._formItem.label
    this._formItem.label = ''
    return this.formItem({
      component: DropdownOptions,
      _options: typeof options === 'string' ? Options.use(options) : options,
      title
    })
  }

  select(select: string | Select, params?: any) {
    return this.formItem({
      component: SearchSelectForm,
      _select: typeof select === 'string' ? Select.use(select).params(params) : select
    })
  }

  listeners(events: { [prop: string]: any }) {
    if (!this._formItem) return this
    if (!this._formItem.on) {
      this._formItem.on = {}
    }
    Object.keys(events).forEach(key => {
      if (this._formItem.on) {
        this._formItem.on[key] = events[key]
      }
    })
    return this
  }

  on(event: string, cb: any) {
    if (!event) return this
    const events = { [event]: cb }
    return this.listeners(events)
  }

  attrs(attrs: { [prop: string]: any }) {
    if (!this._formItem.formItem) return this
    if (typeof this._formItem.formItem === 'string') {
      this._formItem.formItem = { component: this._formItem.formItem }
    }
    Object.keys(attrs).forEach(key => {
      if (!['component', 'on'].includes(key) && this._formItem.formItem) {
        this._formItem.formItem[key] = attrs[key]
      }
    })
    return this
  }

  bind(attr: string, prop: any) {
    if (!attr) return this
    const attrs = { [attr]: prop }
    return this.attrs(attrs)
  }

  attrsFormItem(attrs: { [prop: string]: any }) {
    if (!this._formItem) return this
    Object.keys(attrs).forEach(key => {
      this._formItem[key] = attrs[key]
    })
    return this
  }

  bindFormItem(attr: string, prop: any) {
    if (!attr) return this
    const attrs = { [attr]: prop }
    return this.attrsFormItem(attrs)
  }

  disabled(disabled: boolean | ((vm: any) => boolean) = true) {
    return this.attrs({ disabled })
  }

  placeholder(placeholder = 'common.input') {
    placeholder = this.prefix && placeholder.indexOf('.') === -1 ? `${this.prefix}.${placeholder}` : placeholder
    return this.bind('placeholder', placeholder)
  }

  rules(rules: object | any[]) {
    if (!this._formItem.rules) {
      this._formItem.rules = []
    }
    this._formItem.rules = [...this._formItem.rules, ...getRules(rules, this.prefix)]
    return this
  }

  required(message = '', trigger?: string, required: any = true) {
    if (!message) {
      const component = typeof this._formItem.formItem === 'string' ? this._formItem.formItem : this._formItem.formItem && this._formItem.formItem.component
      if (component && ['el-input', 'el-input-number'].includes(component as string)) {
        message = 'common.input'
        trigger = trigger || 'blur'
      } else {
        message = 'common.select'
        trigger = trigger || 'change'
      }
    }
    trigger = trigger || 'blur'
    return this.rules({ required, message, trigger })
  }

  listRequired(message = '') {
    function validator(rule: any[], value: any[], callback: (err?: any) => void) {
      if (value && value.length) {
        callback()
      } else {
        callback(new Error())
      }
    }
    message = message || 'common.listRequired'
    return this.rules({ validator, message, trigger: 'change' })
  }

  type(type: FormType = 'modify') {
    this._type = type
    return this
  }

  // 后面的方法是和vue模板相关的
  // eslint-disable-next-line
  protected async created(vm: any) {}
  // eslint-disable-next-line
  protected async mounted(vm: any) {}
  // eslint-disable-next-line
  protected async destroyed(vm: any) {}
  // eslint-disable-next-line
  protected async submit(data: any) {} // 提交表单的接口

  // eslint-disable-next-line
  protected async formLoaded(vm: any) {} // 完成自定义加载后的回调

  getData() {
    const data: { [prop: string]: any } = {}
    this._formItemMap.forEach((value) => {
      const item = value.item
      const prop = item.prop
      const defaultValue = typeof item.default === 'function' ? item.default(this.vm) : item.default
      if (typeof prop === 'string') {
        data[prop] = defaultValue
      } else {
        const value = defaultValue || []
        prop.forEach((key, index) => {
          data[key] = value[index]
        })
      }
    })
    return data
  }

  tpl(tpl?: any): any {
    // eslint-disable-next-line
    const $form = this
    const template = tpl || Form.template
    const mixins = this.mixins
    const injectVueInstance = {
      beforeCreate() { $form.vm = this }
    }
    return { mixins: [injectVueInstance, mixins, template] }
  }

  name(name: string) {
    this._name = name
    return this
  }

  end(tpl?: any): any {
    // eslint-disable-next-line
    const $form = this
    // eslint-disable-next-line
    const template = this.tpl(tpl)

    const component: any = { render(h: any) { return h(template, { props: { $form }}) } }
    if (this._name) {
      component.name = this._name
    }
    return component
  }
}

Form.injectFormLib(items as GroupItem[], Form.lib)

export default Form
